import { getBookStatus } from '@/api/book';
import VisitorTable from '@/components/bookStatus/VisitorTable';
import { BOOK_LIST_FILTER } from '@/constants/book';
import useUpdateUrlParams from '@/hooks/useUpateUrlParams';
import { BOOK_STATUS, BookSummaryResponse } from '@/types/book';
import { comma } from '@/util/format';
import { Icon, Pagination } from '@successmode-ktp/kui';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

type Props = {
  countList?: BookSummaryResponse;
};

function VisitorStateSection({ countList }: Props) {
  const updateUrlParams = useUpdateUrlParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initPage = searchParams.get('page') || '0';
  const isViewTotalMode = searchParams.get('viewTotal') === 'true';

  const [selectedFilter, setSelectedFilter] = useState<BOOK_STATUS>('');
  const [totalPage, setTotalPage] = useState(5);
  const [isFirst, setIsFirst] = useState(false);
  const [isLast, setIsLast] = useState(false);

  const { data } = useQuery(
    ['bookStatus', isViewTotalMode, selectedFilter, initPage],
    () =>
      getBookStatus({
        reservationStatus: selectedFilter,
        size: 10,
        page: isViewTotalMode ? Number(initPage) : 0,
      }),
    {
      onSuccess: (data) => {
        if (isViewTotalMode) {
          setTotalPage(data.totalPages);
          setIsFirst(data.first);
          setIsLast(data.last);
        }
      },
      keepPreviousData: true,
    },
  );

  const handleChangeFilter = (value: BOOK_STATUS) => {
    setSelectedFilter(value);
    if (isViewTotalMode) {
      updateUrlParams({ page: '0' });
    }
  };

  const hasVisitorList =
    selectedFilter === 'COMPLETED' || selectedFilter === '';

  return (
    <Section>
      <Nav>
        <FilterList>
          {BOOK_LIST_FILTER.map(({ key, value, label }) => (
            <FilterButton
              key={key}
              isSelected={selectedFilter === value}
              onClick={() => handleChangeFilter(value)}
            >
              {label}
              {key !== 'total' && (
                <Count value={value}>
                  {comma(
                    key === 'visitConfirm' && countList
                      ? countList[key as keyof BookSummaryResponse]
                      : 0,
                  )}
                </Count>
              )}
            </FilterButton>
          ))}
        </FilterList>
        {!isViewTotalMode && (
          <ViewButton onClick={() => updateUrlParams({ viewTotal: 'true' })}>
            전체보기
            <Icon.ChevronRight size={18} fillColor='#949494' />
          </ViewButton>
        )}
      </Nav>
      <VisitorTable row={hasVisitorList ? data?.content : []} />
      {isViewTotalMode && hasVisitorList && (
        <Footer>
          <Pagination
            page={Number(initPage) + 1}
            setPage={(newPage) => {
              updateUrlParams({ page: (newPage - 1).toString() });
            }}
            totalPage={totalPage}
            isFirstPage={isFirst}
            isLastPage={isLast}
          />
        </Footer>
      )}
    </Section>
  );
}

const Section = styled.section``;

const Nav = styled.nav`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FilterList = styled.div``;

const FilterButton = styled.button<{ isSelected: boolean }>`
  display: inline-flex;
  gap: 6px;
  align-items: center;
  color: #3a3b3e;
  font-size: 18px;
  font-weight: 500;
  line-height: 27px;
  padding: 8px 12px 5px 12px;
  border-bottom: 3px solid
    ${({ isSelected }) => (isSelected ? '#3A3B3E' : 'transparent')};
`;

const Count = styled.span<{ value: string }>`
  color: ${({ value }) => (value === 'COMPLETED' ? '#246cf6' : '#80848A')};
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
`;

const ViewButton = styled.button`
  display: flex;
  gap: 4px;
  align-items: center;
  color: #000;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
`;

const Footer = styled.footer`
  margin-top: 24px;
`;

export default VisitorStateSection;
